import { maybeFetchCurrentTrackingToken } from 'core_main/api/tracking';
import { TrackingTokensCurrentApiResponse } from 'core_main/api/tracking/types';
import { post } from 'core_main/request';
import { ChatSessionsApiResponse } from 'core_main/api/sessions/types';
import { HandoffConversationParameters } from 'core_main/sdk/handoff/types';
import { HANDOFF_ENDPOINT } from './constants';

type HttpPostFunction = <T>(endpoint: string, options: { body: string }) => Promise<T>;

interface HandoffDependencies {
  fetchTrackingToken: () => Promise<TrackingTokensCurrentApiResponse | void>;
  httpPost: HttpPostFunction;
  endpoint: string;
}

const defaultDeps: HandoffDependencies = {
  fetchTrackingToken: maybeFetchCurrentTrackingToken,
  httpPost: post as HttpPostFunction,
  endpoint: HANDOFF_ENDPOINT,
};

/**
 * Creates a handoff session with historical messages
 * @param parameters - Handoff parameters
 * @param deps - Optional dependencies object for dependency injection (defaults to real implementations)
 * @returns Promise that resolves with the created session (standard chat session response)
 */
export const createHandoff = (
  parameters: HandoffConversationParameters,
  deps: Partial<HandoffDependencies> = {},
): Promise<ChatSessionsApiResponse> => {
  const {
    fetchTrackingToken = defaultDeps.fetchTrackingToken,
    httpPost = defaultDeps.httpPost,
    endpoint = defaultDeps.endpoint,
  } = deps;

  return fetchTrackingToken().then(() => {
    const { active, from, messages, brand, externalId, lang } = parameters;

    const body: Record<string, unknown> = {
      active,
      from,
      messages: messages.map((msg) => ({
        date: msg.date,
        direction: msg.direction,
        body: msg.body,
      })),
      ...(brand && { brand }),
      ...(externalId && { externalId }),
      ...(lang && { lang }),
    };

    return httpPost<ChatSessionsApiResponse>(endpoint, {
      body: JSON.stringify(body),
    });
  });
};
